import React, { useState, useMemo, useCallback } from 'react';
import Table from '../../components/Table/Table';
import { TableColumn } from '../../types';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { useCrewChanges } from '../../hooks/useCrewChanges';
import { useEmployees } from '../../hooks/useEmployees';
import { useBranches } from '../../hooks/useBranches';
import { useRanks } from '../../hooks/useRanks';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import { CrewChange, Employee, Branch } from '../../types';
import { PageHeader, StatsCards } from '../../components/Common';
import type { StatCard } from '../../components/Common';
import { VesselStats, CrewMemberView } from './vesselReportTypes';
import VesselReportFilters from './VesselReportFilters';
import VesselCrewListView from './VesselCrewListView';

const VesselReport: React.FC = () => {
  const queryOptions = useMemo(() => ({
    pagination: { page: 1, pageSize: 1000 },
    sort: [{ field: 'changeDate', order: 'desc' as const }]
  }), []);
  const branchOptions = useMemo(() => ({
    pagination: { page: 1, pageSize: 1000 },
    sort: [{ field: 'name', order: 'asc' as const }]
  }), []);
  const employeeOptions = useMemo(() => ({
    pagination: { page: 1, pageSize: 1000 },
    sort: [{ field: 'name', order: 'asc' as const }]
  }), []);
  const rankOptions = useMemo(() => ({ pagination: { page: 1, pageSize: 1000 } }), []);

  const { data: crewChanges = [], isLoading: loadingCrew } = useCrewChanges(queryOptions);
  const { data: employees = [], isLoading: loadingEmployees } = useEmployees(employeeOptions);
  const { data: branches = [], isLoading: loadingBranches } = useBranches(branchOptions);
  const { data: ranks = [] } = useRanks(rankOptions);
  const isLoading = loadingCrew || loadingEmployees || loadingBranches;

  const [selectedVessel, setSelectedVessel] = useState<string | null>(null);
  const [showCrewList, setShowCrewList] = useState(false);
  const [listType, setListType] = useState<'signed-on' | 'on-vacation'>('signed-on');

  const [searchTerm, setSearchTerm] = useState('');
  const [crewSizeFilter, setCrewSizeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [vesselFilter, setVesselFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const employeeMap = useMemo(() => {
    const map = new Map<string, Employee>();
    employees.forEach(emp => {
      map.set(String(emp.id), emp);
      if (emp.documentId) map.set(emp.documentId, emp);
    });
    return map;
  }, [employees]);

  const branchMap = useMemo(() => {
    const map = new Map<string, Branch>();
    branches.forEach(b => {
      map.set(String(b.id), b);
      if (b.documentId) map.set(b.documentId, b);
    });
    return map;
  }, [branches]);

  const rankMap = useMemo(() => {
    const map = new Map<string, string>();
    ranks.forEach((r: any) => {
      map.set(String(r.id), r.name || r.title || '');
      if (r.documentId) map.set(r.documentId, r.name || r.title || '');
    });
    return map;
  }, [ranks]);

  // The relation may arrive as a populated object, a mapped *Id documentId,
  // or a plain id on old records — never stringify the object itself.
  const ccEmployeeId = (cc: CrewChange): string => {
    const obj: any = typeof cc.employee === 'object' && cc.employee ? cc.employee : cc.employeeData;
    return String(cc.employeeId || obj?.documentId || obj?.id || (typeof cc.employee !== 'object' ? cc.employee : '') || '');
  };
  const ccBranchId = (cc: CrewChange): string => {
    const obj: any = typeof cc.branch === 'object' && cc.branch ? cc.branch : cc.branchData;
    return String(cc.branchId || obj?.documentId || obj?.id || (typeof cc.branch !== 'object' ? cc.branch : '') || '');
  };

  const vessels = useMemo(() => {
    return branches.filter(b => b.type === 'vessel' && b.status === 'active');
  }, [branches]);

  const { crewMemberViews, vesselStats } = useMemo(() => {
    const latestByEmployee = new Map<string, CrewChange>();
    const changesByEmployee = new Map<string, CrewChange[]>();
    crewChanges.forEach(cc => {
      const empId = ccEmployeeId(cc);
      if (!empId) return;
      const list = changesByEmployee.get(empId) || [];
      list.push(cc);
      changesByEmployee.set(empId, list);
      const existing = latestByEmployee.get(empId);
      if (!existing || new Date(cc.changeDate) > new Date(existing.changeDate)) {
        latestByEmployee.set(empId, cc);
      }
    });
    changesByEmployee.forEach(list => list.sort((a, b) => new Date(b.changeDate).getTime() - new Date(a.changeDate).getTime()));

    const views: CrewMemberView[] = [];
    const statsMap: Record<string, VesselStats> = {};

    vessels.forEach(vessel => {
      statsMap[vessel.id] = {
        vesselId: vessel.id, vesselName: vessel.name,
        signedOnCrew: 0, onVacationCrew: 0, totalCrew: 0,
        reportDate: new Date().toISOString().split('T')[0]
      };
    });

    latestByEmployee.forEach((cc) => {
      const empId = ccEmployeeId(cc);
      const empObj: any = typeof cc.employee === 'object' && cc.employee ? cc.employee : cc.employeeData;
      const emp = employeeMap.get(empId) || empObj;
      if (!emp) return;
      const branchId = ccBranchId(cc) || String(emp.branchId || '');
      const branchObj: any = typeof cc.branch === 'object' && cc.branch ? cc.branch : cc.branchData;
      const branch = branchMap.get(branchId) || branchObj;
      if (!branch || branch.type !== 'vessel') return;

      // A signed-off crew member is home on leave — the report counts them
      // under "on vacation" until their next sign-on.
      let viewStatus: 'signed-on' | 'on-vacation' = 'on-vacation';
      if (cc.status === 'sign_on' || cc.status === 'continue') viewStatus = 'signed-on';

      const empRank = emp.rank || (emp as any).rankData?.name || rankMap.get(String((emp as any).rankId || '')) || emp.position || '';

      // A "continue" extends the stint but keeps the original boarding date —
      // walk back to the actual sign_on change that started it.
      let signOnDate: string | undefined;
      if (cc.status === 'sign_on' || cc.status === 'continue') {
        const history = changesByEmployee.get(empId) || [];
        const boarding = history.find(h =>
          h.status === 'sign_on' &&
          new Date(h.changeDate) <= new Date(cc.changeDate) &&
          (!branchId || ccBranchId(h) === branchId || !ccBranchId(h)));
        signOnDate = (boarding || cc).changeDate;
      }

      views.push({
        id: emp.id, name: emp.name, rank: empRank,
        vesselId: branch.id, vesselName: branch.name, status: viewStatus,
        signOnDate,
        signOffDate: cc.status === 'sign_off' ? cc.changeDate : undefined,
        nationalId: emp.nationalId || '', phone: emp.phoneNumber || ''
      });

      if (statsMap[branch.id]) {
        statsMap[branch.id].totalCrew++;
        if (viewStatus === 'signed-on') statsMap[branch.id].signedOnCrew++;
        else statsMap[branch.id].onVacationCrew++;
      }
    });

    return { crewMemberViews: views, vesselStats: Object.values(statsMap) };
  }, [crewChanges, vessels, employeeMap, branchMap, rankMap]);

  const filteredVesselStats = useMemo(() => {
    return vesselStats.filter(vessel => {
      const matchesSearch = vessel.vesselName.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCrewSize = crewSizeFilter === '' || (() => {
        switch (crewSizeFilter) {
          case 'small': return vessel.totalCrew <= 2;
          case 'medium': return vessel.totalCrew >= 3 && vessel.totalCrew <= 5;
          case 'large': return vessel.totalCrew > 5;
          default: return true;
        }
      })();
      const matchesStatus = statusFilter === '' || (() => {
        switch (statusFilter) {
          case 'fully-operational': return vessel.signedOnCrew > 0 && vessel.onVacationCrew === 0;
          case 'partial-crew': return vessel.signedOnCrew > 0 && vessel.onVacationCrew > 0;
          case 'vacation-heavy': return vessel.onVacationCrew >= vessel.signedOnCrew;
          case 'no-active-crew': return vessel.signedOnCrew === 0;
          default: return true;
        }
      })();
      const matchesVessel = vesselFilter === '' || vessel.vesselId === vesselFilter;
      const reportDate = new Date(vessel.reportDate);
      const matchesDateFrom = !dateFrom || reportDate >= new Date(dateFrom);
      const matchesDateTo = !dateTo || reportDate <= new Date(dateTo);
      return matchesSearch && matchesCrewSize && matchesStatus && matchesVessel && matchesDateFrom && matchesDateTo;
    });
  }, [vesselStats, searchTerm, crewSizeFilter, statusFilter, vesselFilter, dateFrom, dateTo]);

  const getCrewForVessel = useCallback((vesselId: string, status: 'signed-on' | 'on-vacation'): CrewMemberView[] => {
    return crewMemberViews.filter(crew => crew.vesselId === vesselId && crew.status === status);
  }, [crewMemberViews]);

  const handleShowCrewList = (vesselId: string, type: 'signed-on' | 'on-vacation') => {
    setSelectedVessel(vesselId);
    setListType(type);
    setShowCrewList(true);
  };

  const handleExport = () => {
    const exportData = filteredVesselStats.map(vessel => ({
      'Vessel Name': vessel.vesselName,
      'Signed On Crew': vessel.signedOnCrew,
      'On Vacation Crew': vessel.onVacationCrew,
      'Total Crew': vessel.totalCrew,
      'Crew Capacity Utilization': `${Math.round((vessel.signedOnCrew / (vessel.totalCrew || 1)) * 100)}%`
    }));
    exportToExcel(exportData, `vessel_report_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    const exportData = filteredVesselStats.map(vessel => ({
      'Vessel Name': vessel.vesselName,
      'Signed On Crew': vessel.signedOnCrew,
      'On Vacation Crew': vessel.onVacationCrew,
      'Total Crew': vessel.totalCrew,
      'Crew Capacity Utilization': `${Math.round((vessel.signedOnCrew / (vessel.totalCrew || 1)) * 100)}%`
    }));
    printAsExcel(exportData, 'Vessel Report');
  };

  const filteredCrew = useMemo(() => {
    if (!selectedVessel || !showCrewList) return [];
    return getCrewForVessel(selectedVessel, listType);
  }, [selectedVessel, showCrewList, listType, getCrewForVessel]);

  const selectedVesselName = useMemo(() => {
    if (!selectedVessel) return '';
    return vesselStats.find(v => v.vesselId === selectedVessel)?.vesselName || '';
  }, [selectedVessel, vesselStats]);

  const totalStats = useMemo(() => {
    return filteredVesselStats.reduce(
      (acc, vessel) => ({
        totalVessels: acc.totalVessels + 1,
        totalSignedOn: acc.totalSignedOn + vessel.signedOnCrew,
        totalOnVacation: acc.totalOnVacation + vessel.onVacationCrew,
        grandTotal: acc.grandTotal + vessel.totalCrew
      }),
      { totalVessels: 0, totalSignedOn: 0, totalOnVacation: 0, grandTotal: 0 }
    );
  }, [filteredVesselStats]);

  const uniqueVessels = useMemo(() => {
    return vesselStats.map(vessel => ({ id: vessel.vesselId, name: vessel.vesselName }));
  }, [vesselStats]);

  const vesselColumns: TableColumn[] = [
    { key: 'vesselName', label: 'Vessel Name', sortable: true, render: (value: string) => <div className="font-medium text-gray-900">{value}</div> },
    { key: 'reportDate', label: 'Report Date', sortable: true, render: (value: string) => <div className="text-gray-900">{new Date(value).toLocaleDateString()}</div> },
    { key: 'signedOnCrew', label: 'Signed On Crew', sortable: true, render: (value: number, item: VesselStats) => (
      <button onClick={() => handleShowCrewList(item.vesselId, 'signed-on')} className="px-4 py-2 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 transition-colors duration-200 font-semibold">
        {value} crew members
      </button>
    )},
    { key: 'onVacationCrew', label: 'On Vacation', sortable: true, render: (value: number, item: VesselStats) => (
      <button onClick={() => handleShowCrewList(item.vesselId, 'on-vacation')} className="px-4 py-2 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors duration-200 font-semibold">
        {value} crew members
      </button>
    )},
    { key: 'totalCrew', label: 'Total Crew', sortable: true, render: (value: number) => <div className="text-lg font-bold text-gray-900">{value}</div> }
  ];

  const stats: StatCard[] = [
    { label: 'Total Vessels', value: totalStats.totalVessels, color: 'blue' },
    { label: 'Crew Signed On', value: totalStats.totalSignedOn, color: 'green' },
    { label: 'On Vacation', value: totalStats.totalOnVacation, color: 'yellow' },
    { label: 'Total Crew', value: totalStats.grandTotal, color: 'purple' },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Vessel Report"
        description="Monitor crew deployment and vacation status across all vessels"
        actions={[
          { label: 'Export', onClick: handleExport, variant: 'success' },
          { label: 'Print', onClick: handlePrint, variant: 'info' },
        ]}
      />

      <StatsCards stats={stats} />

      {!showCrewList && (
        <VesselReportFilters
          searchTerm={searchTerm} onSearchChange={setSearchTerm}
          vesselFilter={vesselFilter} onVesselFilterChange={setVesselFilter}
          crewSizeFilter={crewSizeFilter} onCrewSizeFilterChange={setCrewSizeFilter}
          statusFilter={statusFilter} onStatusFilterChange={setStatusFilter}
          dateFrom={dateFrom} onDateFromChange={setDateFrom}
          dateTo={dateTo} onDateToChange={setDateTo}
          uniqueVessels={uniqueVessels}
          filteredCount={filteredVesselStats.length} totalCount={vesselStats.length}
          onClearFilters={() => { setSearchTerm(''); setCrewSizeFilter(''); setStatusFilter(''); setVesselFilter(''); setDateFrom(''); setDateTo(''); }}
          onFilterToday={() => { const today = new Date().toISOString().split('T')[0]; setDateFrom(today); setDateTo(today); }}
        />
      )}

      {!showCrewList ? (
        <div className="card">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Vessel Crew Status</h2>
            <p className="text-sm text-gray-600">Click on crew numbers to view detailed crew lists</p>
          </div>
          {isLoading ? (
            <TableSkeleton />
          ) : (
            <Table data={filteredVesselStats} columns={vesselColumns} searchable={false} />
          )}
        </div>
      ) : (
        <VesselCrewListView
          vesselName={selectedVesselName}
          listType={listType}
          crewData={filteredCrew}
          onBack={() => setShowCrewList(false)}
        />
      )}
    </div>
  );
};

export default VesselReport;
